import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { SignedAvatarImage } from "@/components/signed-avatar-image";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { CheckIcon, UserIcon } from "lucide-react";
import { ReactNode, useEffect, useState } from "react";
import useSWR from "swr";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { groupUsersByRole, roleLabel } from "@/lib/swr";
import type { RoleValue } from "@/lib/swr";
import { cn } from "@/lib/utils";

export type PickerUser = {
  id: string | number;
  name?: string;
  email?: string;
  avatarThumb?: string;
  roles?: string[];
  kind?: "human" | "bot";
};

type UserPickerDialogProps = {
  multiple: boolean;
  selected: PickerUser[];
  onConfirm: (users: PickerUser[]) => void;
  title: string;
  description: string;
  buttonText?: ReactNode;
  hideUsers?: PickerUser[];
  className?: string;
  icon?: ReactNode;
  /** Custom trigger element (rendered asChild), replacing the default button. */
  trigger?: ReactNode;
  /** Default to users holding one of these roles, with a "Show all" escape hatch. */
  roleFilter?: RoleValue[];
  /** Endpoint the candidate list is fetched from (default "/users"). Point it
   * at a server-scoped listing (e.g. "/apps/:id/members") instead of
   * filtering client-side — the picker renders exactly what the server
   * returns; "Show all" only lifts the role filter. */
  sourceUrl?: string;
  /** Muted hint under the title (e.g. how to get someone onto the list). */
  hint?: string;
};

// Shared user-picker dialog (single or multiple) backing UserSelector /
// UsersSelector: fetch /users, search via Command, toggle selection, confirm.
export function UserPickerDialog({
  multiple,
  selected,
  onConfirm,
  title,
  description,
  buttonText,
  hideUsers = [],
  className,
  icon,
  trigger,
  roleFilter,
  sourceUrl,
  hint,
}: UserPickerDialogProps) {
  const { data: users } = useSWR<PickerUser[]>([sourceUrl ?? "/users"]);
  const [local, setLocal] = useState<PickerUser[]>(selected);
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (open) setLocal(selected);
  }, [open, selected]);

  if (!users) return null;

  const withoutHidden = users.filter(
    (u) => !hideUsers.some((h) => h.id === u.id)
  );
  const filtered =
    roleFilter?.length && !showAll
      ? withoutHidden.filter((u) =>
          u.roles?.some((r) => (roleFilter as string[]).includes(r))
        )
      : withoutHidden;
  // Humans grouped by role (same buckets as the Members tab / Admin Users;
  // multi-role users repeat per group), bots in their own trailing group.
  // Under an active role filter only the filtered roles' groups remain.
  const humans = filtered.filter((u) => u.kind !== "bot");
  const bots = filtered.filter((u) => u.kind === "bot");
  const roleGroups = groupUsersByRole(humans).filter(
    (g) =>
      !roleFilter?.length || showAll || (roleFilter as string[]).includes(g.key)
  );
  const groups = bots.length
    ? [...roleGroups, { key: "bot", label: "Bots", users: bots }]
    : roleGroups;
  const isSelected = (u: PickerUser) => local.some((s) => s.id === u.id);

  const toggle = (u: PickerUser) => {
    if (multiple) {
      setLocal((prev) =>
        prev.some((s) => s.id === u.id)
          ? prev.filter((s) => s.id !== u.id)
          : [...prev, u]
      );
    } else {
      setLocal([u]);
    }
  };

  const handleContinue = () => {
    onConfirm(local);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button
            variant="outline"
            size="sm"
            className={cn("h-6 px-2 py-0 shadow-none", className)}
          >
            {icon ?? <UserIcon className="h-4 w-4" />}
            {buttonText}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent
        className="gap-0 p-0 outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader className="px-4 pb-4 pt-5">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
          {!!roleFilter?.length && (
            <Tabs
              value={showAll ? "all" : "role"}
              onValueChange={(v) => setShowAll(v === "all")}
            >
              <TabsList>
                <TabsTrigger value="role">
                  {roleFilter.map(roleLabel).join(" / ")}
                </TabsTrigger>
                <TabsTrigger value="all">
                  Show all
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}
        </DialogHeader>
        <Command className="overflow-hidden rounded-t-none border-t bg-transparent">
          <CommandInput placeholder="Search user…" />
          <CommandList>
            <CommandEmpty>No results.</CommandEmpty>
            {groups.map((g) => (
              <CommandGroup key={g.key} heading={g.label} className="p-2">
                {g.users.map((user) => (
                  <CommandItem
                    key={String(user.id)}
                    // Unique per group (a user can sit in several role
                    // groups); cmdk matches the search against name/email.
                    value={`${g.key}:${user.id}`}
                    keywords={[user.name ?? "", user.email ?? ""]}
                    className="flex items-center px-2"
                    onSelect={() => toggle(user)}
                  >
                    <Avatar>
                      <SignedAvatarImage
                        src={user.avatarThumb}
                        alt={user.name}
                      />
                      <AvatarFallback>{user.name?.[0]}</AvatarFallback>
                    </Avatar>
                    <div className="ml-2">
                      <p className="text-sm font-medium leading-none">
                        {user.name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {user.email}
                      </p>
                    </div>
                    {isSelected(user) && (
                      <CheckIcon className="ml-auto flex h-5 w-5 text-primary" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
        <DialogFooter className="flex items-center border-t p-4 sm:justify-between">
          {local.length > 0 ? (
            <div className="flex max-w-[300px] flex-wrap items-center gap-2">
              {local.map((user) => (
                <div key={user.id} className="flex items-center gap-2">
                  <Avatar
                    className={cn(
                      "border-2 border-background",
                      multiple &&
                        "cursor-pointer transition-opacity hover:opacity-80"
                    )}
                    onClick={multiple ? () => toggle(user) : undefined}
                  >
                    <SignedAvatarImage src={user.avatarThumb} />
                    <AvatarFallback>{user.name?.[0]}</AvatarFallback>
                  </Avatar>
                  {!multiple && (
                    <p className="text-sm font-medium">{user.name}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
          <Button
            onClick={handleContinue}
            disabled={!multiple && local.length === 0}
          >
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export type UserSelectorProps = {
  selectedUser?: PickerUser | null;
  onContinue: (user: PickerUser | null) => void;
  buttonText?: ReactNode;
  hideUsers?: PickerUser[];
  className?: string;
  icon?: ReactNode;
  /** Custom trigger element (rendered asChild), replacing the default button. */
  trigger?: ReactNode;
  /** Override the dialog title/description (defaults suit thread member picking). */
  title?: string;
  description?: string;
  /** Default to users holding one of these roles, with a "Show all" escape hatch. */
  roleFilter?: RoleValue[];
  /** Candidate-list endpoint and hint (see UserPickerDialog). */
  sourceUrl?: string;
  hint?: string;
};

// Single-user picker.
export function UserSelector({
  selectedUser,
  onContinue,
  ...rest
}: UserSelectorProps) {
  return (
    <UserPickerDialog
      multiple={false}
      selected={selectedUser ? [selectedUser] : []}
      onConfirm={(users) => onContinue(users[0] ?? null)}
      title="Select a user"
      description="Choose a user to add to this thread."
      {...rest}
    />
  );
}

export default UserSelector;
